<template>
  <div>
    <b-card no-body>
      <b-card-body>
        <validation-observer ref="refFormObserver">
          <b-form>
            <div class="d-flex justify-content-between align-items-center mb-2">
              <h5 class="mb-0">Profiles</h5>
              <div class="d-flex gap-1">
                <b-button variant="primary" size="sm" @click="addNewProfile" :disabled="isLoadingProfiles"
                  class="text-capitalize">
                  <feather-icon icon="PlusIcon" size="14" class="mr-25" />
                  {{ $t("dmp.profiles.addProfile") }}
                </b-button>
              </div>
            </div>
            <!-- Loading spinner -->
            <div v-if="isLoadingProfiles" class="text-center py-4">
              <b-spinner variant="primary" :label="$t('dmp.profiles.loadingProfiles')"></b-spinner>
              <p class="mt-2 mb-0">{{ $t("dmp.profiles.loadingProfiles") }}</p>
            </div>

            <!-- Header with Add button -->

            <b-row v-if="!isLoadingProfiles">
              <b-col cols="4" class="table-column">
                <!-- Profiles Table -->
                <b-table :items="profiles" :fields="profileTableFields" responsive striped hover show-empty
                  :empty-text="$t('dmp.profiles.noProfilesConfigured')" selectable select-mode="single"
                  @row-selected="onRowSelected" sort-by="number" :sort-desc="false" class="mb-0">
                  <template #cell(number)="data">
                    {{ formatProfileNumber(data.item.profileNumber || data.item.number) }}
                  </template>

                  <template #cell(name)="data">
                    {{ data.item.name }}
                  </template>
                </b-table>
              </b-col>

              <b-col cols="8" class="details-column">
                <!-- Selected Profile Details Card -->
                <b-card v-if="selectedProfile" no-body>
                  <b-card-header class="mb-1 card-header-custom pt-0">
                    <h6 class="mb-0">{{ $t("dmp.profiles.profileDetails") }}</h6>
                  </b-card-header>
                  <b-card-body>
                    <validation-observer ref="detailFormObserver">
                      <b-form @submit.prevent="saveProfile">
                        <b-row>
                          <!-- Left Column -->
                          <b-col cols="12" md="6">
                            <b-form-group :label="$t('dmp.profiles.profileNumber')" label-for="profileNumber">
                              <validation-provider #default="{ errors }" :name="$t('dmp.profiles.profileNumber')"
                                rules="required|numeric|min_value:1|max_value:99">
                                <b-form-input id="profileNumber" v-model.number="selectedProfile.profileNumber"
                                  type="number" :state="errors.length > 0 ? false : null" :disabled="isEditing" min="1"
                                  max="99" :placeholder="$t('dmp.profiles.profileNumberHint')" />
                                <b-form-invalid-feedback>
                                  {{ errors[0] }}
                                </b-form-invalid-feedback>
                              </validation-provider>
                            </b-form-group>

                            <b-form-group :label="$t('dmp.profiles.profileName')" label-for="profileName">
                              <validation-provider #default="{ errors }" :name="$t('dmp.profiles.profileName')"
                                rules="required">
                                <b-form-input id="profileName" v-model="selectedProfile.name" type="text"
                                  :state="errors.length > 0 ? false : null" maxlength="32"
                                  :placeholder="$t('dmp.profiles.profileNameHint')" />
                                <b-form-invalid-feedback>
                                  {{ errors[0] }}
                                </b-form-invalid-feedback>
                              </validation-provider>
                            </b-form-group>

                            <b-form-group :label="$t('dmp.profiles.armDisarmAreas')" label-for="armDisarmAreas">
                              <validation-provider #default="{ errors }" :name="$t('dmp.profiles.armDisarmAreas')">
                                <b-form-input id="armDisarmAreas" v-model="selectedProfile.armDisarmAreas" type="text"
                                  :state="errors.length > 0 ? false : null"
                                  :placeholder="$t('dmp.profiles.armDisarmAreasHint')" />
                                <b-form-invalid-feedback>
                                  {{ errors[0] }}
                                </b-form-invalid-feedback>
                              </validation-provider>
                            </b-form-group>

                            <b-form-group :label="$t('dmp.profiles.accessAreas')" label-for="accessAreas">
                              <validation-provider #default="{ errors }" :name="$t('dmp.profiles.accessAreas')">
                                <b-form-input id="accessAreas" v-model="selectedProfile.accessAreas" type="text"
                                  :state="errors.length > 0 ? false : null"
                                  :placeholder="$t('dmp.profiles.accessAreasHint')" />
                                <b-form-invalid-feedback>
                                  {{ errors[0] }}
                                </b-form-invalid-feedback>
                              </validation-provider>
                            </b-form-group>

                            <b-form-group :label="$t('dmp.profiles.outputGroup')" label-for="outputGroup">
                              <validation-provider #default="{ errors }" :name="$t('dmp.profiles.outputGroup')"
                                rules="required|numeric|min_value:0|max_value:20">
                                <b-form-input id="outputGroup" v-model.number="selectedProfile.outputGroup"
                                  type="number" :state="errors.length > 0 ? false : null" min="0" max="20"
                                  :placeholder="$t('dmp.profiles.outputGroupHint')" />
                                <b-form-invalid-feedback>
                                  {{ errors[0] }}
                                </b-form-invalid-feedback>
                              </validation-provider>
                            </b-form-group>

                            <b-form-group :label="$t('dmp.profiles.reArmDelay')" label-for="reArmDelay">
                              <validation-provider #default="{ errors }" :name="$t('dmp.profiles.reArmDelay')"
                                rules="required|numeric|min_value:0|max_value:720">
                                <b-form-input id="reArmDelay" v-model.number="selectedProfile.reArmDelay" type="number"
                                  :state="errors.length > 0 ? false : null" min="0" max="720"
                                  :placeholder="$t('dmp.profiles.reArmDelayHint')" />
                                <b-form-invalid-feedback>
                                  {{ errors[0] }}
                                </b-form-invalid-feedback>
                              </validation-provider>
                            </b-form-group>

                            <b-form-group :label="$t('dmp.profiles.inactiveUserAuditDays')"
                              label-for="inactiveUserAuditDays">
                              <validation-provider #default="{ errors }"
                                :name="$t('dmp.profiles.inactiveUserAuditDays')"
                                rules="required|numeric|min_value:0|max_value:450">
                                <b-form-input id="inactiveUserAuditDays"
                                  v-model.number="selectedProfile.inactiveUserAuditDays" type="number"
                                  :state="errors.length > 0 ? false : null" min="0" max="450" :placeholder="$t('dmp.profiles.inactiveUserAuditDaysHint')
                                    " />
                                <b-form-invalid-feedback>
                                  {{ errors[0] }}
                                </b-form-invalid-feedback>
                              </validation-provider>
                            </b-form-group>
                          </b-col>

                          <!-- Right Column - Permissions -->
                          <b-col cols="12" md="6">
                            <h6 class="mb-1 font-weight-bold">
                              {{ $t("dmp.profiles.permissions") }}
                            </h6>
                            <b-row>
                              <b-col cols="12" md="6">
                                <b-form-checkbox v-model="selectedProfile.permissions.arm" :disabled="isSavingProfile"
                                  class="mb-75">
                                  {{ $t("dmp.profiles.arm") }}
                                </b-form-checkbox>
                              </b-col>
                              <b-col cols="12" md="6">
                                <b-form-checkbox v-model="selectedProfile.permissions.userCodes"
                                  :disabled="isSavingProfile" class="mb-75">
                                  {{ $t("dmp.profiles.userCodes") }}
                                </b-form-checkbox>
                              </b-col>
                              <b-col cols="12" md="6">
                                <b-form-checkbox v-model="selectedProfile.permissions.disarm"
                                  :disabled="isSavingProfile" class="mb-75">
                                  {{ $t("dmp.profiles.disarm") }}
                                </b-form-checkbox>
                              </b-col>
                              <b-col cols="12" md="6">
                                <b-form-checkbox v-model="selectedProfile.permissions.schedules"
                                  :disabled="isSavingProfile" class="mb-75">
                                  {{ $t("dmp.profiles.schedules") }}
                                </b-form-checkbox>
                              </b-col>
                              <b-col cols="12" md="6">
                                <b-form-checkbox v-model="selectedProfile.permissions.alarmSilence"
                                  :disabled="isSavingProfile" class="mb-75">
                                  {{ $t("dmp.profiles.alarmSilence") }}
                                </b-form-checkbox>
                              </b-col>
                              <b-col cols="12" md="6">
                                <b-form-checkbox v-model="selectedProfile.permissions.setTime"
                                  :disabled="isSavingProfile" class="mb-75">
                                  {{ $t("dmp.profiles.setTime") }}
                                </b-form-checkbox>
                              </b-col>
                              <b-col cols="12" md="6">
                                <b-form-checkbox v-model="selectedProfile.permissions.lockdown"
                                  :disabled="isSavingProfile" class="mb-75">
                                  {{ $t("dmp.profiles.lockdown") }}
                                </b-form-checkbox>
                              </b-col>
                              <b-col cols="12" md="6">
                                <b-form-checkbox v-model="selectedProfile.permissions.displayEvents"
                                  :disabled="isSavingProfile" class="mb-75">
                                  {{ $t("dmp.profiles.displayEvents") }}
                                </b-form-checkbox>
                              </b-col>
                              <b-col cols="12" md="6">
                                <b-form-checkbox v-model="selectedProfile.permissions.doorLockUnlock"
                                  :disabled="isSavingProfile" class="mb-75">
                                  {{ $t("dmp.profiles.doorLockUnlock") }}
                                </b-form-checkbox>
                              </b-col>
                              <b-col cols="12" md="6">
                                <b-form-checkbox v-model="selectedProfile.permissions.serviceRequest"
                                  :disabled="isSavingProfile" class="mb-75">
                                  {{ $t("dmp.profiles.serviceRequest") }}
                                </b-form-checkbox>
                              </b-col>
                              <b-col cols="12" md="6">
                                <b-form-checkbox v-model="selectedProfile.permissions.sensorReset"
                                  :disabled="isSavingProfile" class="mb-75">
                                  {{ $t("dmp.profiles.sensorReset") }}
                                </b-form-checkbox>
                              </b-col>
                              <b-col cols="12" md="6">
                                <b-form-checkbox v-model="selectedProfile.permissions.fireDrill"
                                  :disabled="isSavingProfile" class="mb-75">
                                  {{ $t("dmp.profiles.fireDrill") }}
                                </b-form-checkbox>
                              </b-col>
                              <b-col cols="12" md="6">
                                <b-form-checkbox v-model="selectedProfile.permissions.doorAccess"
                                  :disabled="isSavingProfile" class="mb-75">
                                  {{ $t("dmp.profiles.doorAccess") }}
                                </b-form-checkbox>
                              </b-col>
                              <b-col cols="12" md="6">
                                <b-form-checkbox v-model="selectedProfile.permissions.extend"
                                  :disabled="isSavingProfile" class="mb-75">
                                  {{ $t("dmp.profiles.extend") }}
                                </b-form-checkbox>
                              </b-col>
                              <b-col cols="12" md="6">
                                <b-form-checkbox v-model="selectedProfile.permissions.armedAreas"
                                  :disabled="isSavingProfile" class="mb-75">
                                  {{ $t("dmp.profiles.armedAreas") }}
                                </b-form-checkbox>
                              </b-col>
                              <b-col cols="12" md="6">
                                <b-form-checkbox v-model="selectedProfile.permissions.tempCode"
                                  :disabled="isSavingProfile" class="mb-75">
                                  {{ $t("dmp.profiles.tempCode") }}
                                </b-form-checkbox>
                              </b-col>
                              <b-col cols="12" md="6">
                                <b-form-checkbox v-model="selectedProfile.permissions.outputsOnOff"
                                  :disabled="isSavingProfile" class="mb-75">
                                  {{ $t("dmp.profiles.outputsOnOff") }}
                                </b-form-checkbox>
                              </b-col>
                              <b-col cols="12" md="6">
                                <b-form-checkbox v-model="selectedProfile.permissions.antiPassback"
                                  :disabled="isSavingProfile" class="mb-75">
                                  {{ $t("dmp.profiles.antiPassback") }}
                                </b-form-checkbox>
                              </b-col>
                              <b-col cols="12" md="6">
                                <b-form-checkbox v-model="selectedProfile.permissions.zoneStatus"
                                  :disabled="isSavingProfile" class="mb-75">
                                  {{ $t("dmp.profiles.zoneStatus") }}
                                </b-form-checkbox>
                              </b-col>
                              <b-col cols="12" md="6">
                                <b-form-checkbox v-model="selectedProfile.permissions.easyArmDisarm"
                                  :disabled="isSavingProfile" class="mb-75">
                                  {{ $t("dmp.profiles.easyArmDisarm") }}
                                </b-form-checkbox>
                              </b-col>
                              <b-col cols="12" md="6">
                                <b-form-checkbox v-model="selectedProfile.permissions.bypassZones"
                                  :disabled="isSavingProfile" class="mb-75">
                                  {{ $t("dmp.profiles.bypassZones") }}
                                </b-form-checkbox>
                              </b-col>
                              <b-col cols="12" md="6">
                                <b-form-checkbox v-model="selectedProfile.permissions.useSecondaryLanguage
                                  " :disabled="isSavingProfile" class="mb-75">
                                  {{ $t("dmp.profiles.useSecondaryLanguage") }}
                                </b-form-checkbox>
                              </b-col>
                              <b-col cols="12" md="6">
                                <b-form-checkbox v-model="selectedProfile.permissions.zoneMonitor"
                                  :disabled="isSavingProfile" class="mb-75">
                                  {{ $t("dmp.profiles.zoneMonitor") }}
                                </b-form-checkbox>
                              </b-col>
                              <b-col cols="12" md="6">
                                <b-form-checkbox v-model="selectedProfile.permissions.cardPlusPIN"
                                  :disabled="isSavingProfile" class="mb-75">
                                  {{ $t("dmp.profiles.cardPlusPIN") }}
                                </b-form-checkbox>
                              </b-col>
                              <b-col cols="12" md="6">
                                <b-form-checkbox v-model="selectedProfile.permissions.systemStatus"
                                  :disabled="isSavingProfile" class="mb-75">
                                  {{ $t("dmp.profiles.systemStatus") }}
                                </b-form-checkbox>
                              </b-col>
                              <b-col cols="12" md="6">
                                <b-form-checkbox v-model="selectedProfile.permissions.wiFiSetup"
                                  :disabled="isSavingProfile" class="mb-75">
                                  {{ $t("dmp.profiles.wiFiSetup") }}
                                </b-form-checkbox>
                              </b-col>
                              <b-col cols="12" md="6">
                                <b-form-checkbox v-model="selectedProfile.permissions.systemTest"
                                  :disabled="isSavingProfile" class="mb-75">
                                  {{ $t("dmp.profiles.systemTest") }}
                                </b-form-checkbox>
                              </b-col>
                              <b-col cols="12" md="6">
                                <b-form-checkbox v-model="selectedProfile.permissions.technicianUser"
                                  :disabled="isSavingProfile" class="mb-75">
                                  {{ $t("dmp.profiles.technicianUser") }}
                                </b-form-checkbox>
                              </b-col>
                              <b-col cols="12" md="6">
                                <b-form-checkbox v-model="selectedProfile.permissions.profiles"
                                  :disabled="isSavingProfile" class="mb-75">
                                  {{ $t("dmp.profiles.title") }}
                                </b-form-checkbox>
                              </b-col>
                              <b-col cols="12" md="6">
                                <b-form-checkbox v-model="selectedProfile.permissions.lockdownOverride"
                                  :disabled="isSavingProfile" class="mb-75">
                                  {{ $t("dmp.profiles.lockdownOverride") }}
                                </b-form-checkbox>
                              </b-col>
                              <b-col cols="12" md="6">
                                <b-form-checkbox v-model="selectedProfile.permissions.dualAuthority"
                                  :disabled="isSavingProfile" class="mb-75">
                                  {{ $t("dmp.profiles.dualAuthority") }}
                                </b-form-checkbox>
                              </b-col>
                            </b-row>
                          </b-col>

                          <!-- Schedules Section -->
                          <b-col cols="12">
                            <h6 class="mt-2 mb-1 font-weight-bold">
                              {{ $t("dmp.profiles.schedules") }}
                            </h6>
                          </b-col>

                          <!-- First 4 schedules -->
                          <b-col cols="12" md="6">
                            <div v-for="index in 4" :key="index" class="mb-1">
                              <div class="d-flex align-items-center">
                                <label class="mb-0 mr-1" style="min-width: 20px">{{ index }}.</label>
                                <b-form-select v-model="selectedProfile.schedules[index - 1]"
                                  :options="scheduleSelectOptions" class="flex-grow-1" />
                              </div>
                            </div>
                          </b-col>

                          <b-col cols="12" md="6">
                            <div v-for="index in 4" :key="index + 4" class="mb-1">
                              <div class="d-flex align-items-center">
                                <label class="mb-0 mr-1" style="min-width: 20px">{{ index + 4 }}.</label>
                                <b-form-select v-model="selectedProfile.schedules[index + 3]"
                                  :options="scheduleSelectOptions" class="flex-grow-1" />
                              </div>
                            </div>
                          </b-col>

                          <!-- Private Doors Section -->
                          <b-col cols="12">
                            <h6 class="mt-2 mb-1 font-weight-bold">
                              {{ $t("dmp.profiles.privateDoors") }}
                            </h6>
                          </b-col>

                          <!-- First 2 private doors -->
                          <b-col cols="12" md="6">
                            <div v-for="index in 2" :key="'pd-' + index" class="mb-1">
                              <div class="d-flex align-items-center">
                                <label class="mb-0 mr-1" style="min-width: 20px">{{ index }}.</label>
                                <b-form-select v-model="selectedProfile.privateDoors[index - 1]"
                                  :options="privateDoorSelectOptions" class="flex-grow-1" />
                              </div>
                            </div>
                          </b-col>

                          <b-col cols="12" md="6">
                            <div v-for="index in 2" :key="'pd-' + (index + 2)" class="mb-1">
                              <div class="d-flex align-items-center">
                                <label class="mb-0 mr-1" style="min-width: 20px">{{ index + 2 }}.</label>
                                <b-form-select v-model="selectedProfile.privateDoors[index + 1]"
                                  :options="privateDoorSelectOptions" class="flex-grow-1" />
                              </div>
                            </div>
                          </b-col>

                          <!-- Action Buttons -->
                          <b-col cols="12" class="d-flex justify-content-end mt-2">
                            <b-button v-if="isEditing" variant="outline-danger" size="sm" class="mr-1"
                              @click="deleteProfile" :disabled="isSavingProfile || isDeletingProfile">
                              <b-spinner v-if="isDeletingProfile" small class="mr-25" />
                              <feather-icon v-else icon="TrashIcon" size="14" class="mr-25" />
                              {{ $t("actions.delete") }}
                            </b-button>
                            <b-button variant="primary" size="sm" type="submit"
                              :disabled="isSavingProfile || isDeletingProfile">
                              <b-spinner v-if="isSavingProfile" small class="mr-25" />
                              <feather-icon v-else icon="SaveIcon" size="14" class="mr-25" />
                              {{ $t("actions.save") }}
                            </b-button>
                          </b-col>
                        </b-row>
                      </b-form>
                    </validation-observer>
                  </b-card-body>
                </b-card>
              </b-col>
            </b-row>
          </b-form>
        </validation-observer>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import { ref, computed, onMounted, getCurrentInstance, watch } from "@vue/composition-api";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import { useToast } from "vue-toastification/composition";
import formValidation from "@core/comp-functions/forms/form-validation";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import Swal from "sweetalert2";
import store from "@/store";
import { useTemplateMode } from "./useTemplateMode";

export default {
  name: "ConfigProfiles",
  components: {
    ValidationObserver,
    ValidationProvider,
    ToastificationContent,
  },
  props: {
    uuid: {
      type: String,
      required: true,
    },
    profilesData: {
      type: Array,
      default: () => [],
    },
    timeSchedulesData: {
      type: Array,
      default: () => [],
    },
    devicesData: {
      type: Array,
      default: () => [],
    },
    panelId: {
      type: [Number, String],
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    isActive: {
      type: Boolean,
      default: false,
    },
    source: {
      type: String,
      default: "default",
    },
  },
  setup(props, { emit }) {
    const vm = getCurrentInstance();
    const proxy = vm.root.proxy;

    const { t } = useI18nUtils();
    const toast = useToast();
    const { refFormObserver, getValidationState } = formValidation();
    const { isTemplateMode, emitTemplateUpdate, nextSequentialId } = useTemplateMode(props, emit);

    const isLoadingProfiles = computed(() => props.loading);
    const isSavingProfile = ref(false);
    const isDeletingProfile = ref(false);
    const profiles = ref([]);

    const selectedProfile = ref(null);
    const isEditing = ref(false);
    const detailFormObserver = ref(null);


    const timeSchedules = computed(() => props.timeSchedulesData || []);
    const devices = computed(() =>
      (props.devicesData || []).filter((d) => d.privateDoor === "Y")
    );


    const scheduleSelectOptions = computed(() => {
      const options = [{ value: 0, text: proxy.$t("dmp.profiles.noSchedule") }];
      timeSchedules.value.forEach((schedule) => {
        options.push({
          value: parseInt(schedule.scheduleNumber),
          text: `${parseInt(schedule.scheduleNumber)} - ${String(schedule.scheduleName)}`,
        });
      });
      return options;
    });


    const privateDoorSelectOptions = computed(() => {
      const options = [{ value: 0, text: proxy.$t("dmp.profiles.noDoor") }];
      devices.value.forEach((device) => {
        options.push({
          value: parseInt(device.deviceNumber),
          text: `${parseInt((device.deviceNumber))} - ${device.deviceName}`,
        });
      });
      return options;
    });

    const profileTableFields = computed(() => [
      { key: "number", label: proxy.$t("dmp.profiles.number"), sortable: true },
      { key: "name", label: proxy.$t("dmp.profiles.name"), sortable: true },
    ]);

    // Helper function to format profile number (1 -> 1)
    const formatProfileNumber = (value) => {
      if (!value || value === "") return "";
      const num = parseInt(value);
      if (isNaN(num) || num < 1 || num > 99) return "";
      return num.toString();
    };

    // Helper function to parse profile number from API format
    const parseProfileNumber = (value) => {
      if (!value) return "";
      const num = parseInt(value);
      return num > 0 ? num : "";
    };

    const loadProfiles = async () => {
      try {
        if (props.profilesData && props.profilesData.length > 0) {
          profiles.value = props.profilesData.map((item) => {
            const profile = item.profile;
            return {
              profileNumber: profile.profileNumber,
              number: profile.profileNumber,
              name: profile.profileName || "",
              armDisarmAreas: profile.armDisarmAreas || "",
              accessAreas: profile.accessAreas || "",
              outputGroup: parseInt(profile.outputGroup) || 0,
              reArmDelay: parseInt(profile.reArmDelay) || 0,
              inactiveUserAuditDays: parseInt(profile.inactiveUserAuditDays) || 0,
              permissions: {
                arm: profile.permissions.arm || false,
                disarm: profile.permissions.disarm || false,
                alarmSilence: profile.permissions.alarmSilence || false,
                lockdown: profile.permissions.lockdown || false,
                doorLockUnlock: profile.permissions.doorLock || false,
                sensorReset: profile.permissions.sensorReset || false,
                doorAccess: profile.permissions.doorAccess || false,
                armedAreas: profile.permissions.armedAreas || false,
                outputsOnOff: profile.permissions.outputOnOff || false,
                zoneStatus: profile.permissions.zoneStatus || false,
                bypassZones: profile.permissions.bypassZones || false,
                zoneMonitor: profile.permissions.zoneMonitor || false,
                systemStatus: profile.permissions.systemStatus || false,
                systemTest: profile.permissions.systemTest || false,
                profiles: profile.permissions.profiles || false,
                userCodes: profile.permissions.userCodes || false,
                schedules: profile.permissions.schedules || false,
                setTime: profile.permissions.time || false,
                displayEvents: profile.permissions.displayEvents || false,
                serviceRequest: profile.permissions.serviceRequest || false,
                fireDrill: profile.permissions.fireDrill || false,
                extend: profile.permissions.extendSchedule || false,
                tempCode: false,
                antiPassback: profile.permissions.antiPassback || false,
                easyArmDisarm: profile.permissions.easyArm || false,
                useSecondaryLanguage: profile.permissions.secondaryLanguage || false,
                cardPlusPIN: profile.permissions.cardPlusPin || false,
                wiFiSetup: profile.permissions.wifi || false,
                technicianUser: profile.permissions.technicianUser || false,
                lockdownOverride: profile.permissions.lockdownOverride || false,
                dualAuthority: profile.permissions.dualAuthority || false,
              },
              schedules: [
                profile.schedule1 && profile.schedule1 !== "00" && profile.schedule1 !== "--"
                  ? parseInt(profile.schedule1)
                  : 0,
                profile.schedule2 && profile.schedule2 !== "00" && profile.schedule2 !== "--"
                  ? parseInt(profile.schedule2)
                  : 0,
                profile.schedule3 && profile.schedule3 !== "00" && profile.schedule3 !== "--"
                  ? parseInt(profile.schedule3)
                  : 0,
                profile.schedule4 && profile.schedule4 !== "00" && profile.schedule4 !== "--"
                  ? parseInt(profile.schedule4)
                  : 0,
                profile.schedule5 && profile.schedule5 !== "00" && profile.schedule5 !== "--"
                  ? parseInt(profile.schedule5)
                  : 0,
                profile.schedule6 && profile.schedule6 !== "00" && profile.schedule6 !== "--"
                  ? parseInt(profile.schedule6)
                  : 0,
                profile.schedule7 && profile.schedule7 !== "00" && profile.schedule7 !== "--"
                  ? parseInt(profile.schedule7)
                  : 0,
                profile.schedule8 && profile.schedule8 !== "00" && profile.schedule8 !== "--"
                  ? parseInt(profile.schedule8)
                  : 0,
              ],
              privateDoors: [
                profile.privateDoor1 && profile.privateDoor1 !== "00"
                  ? parseInt(profile.privateDoor1)
                  : 0,
                profile.privateDoor2 && profile.privateDoor2 !== "00"
                  ? parseInt(profile.privateDoor2)
                  : 0,
                profile.privateDoor3 && profile.privateDoor3 !== "00"
                  ? parseInt(profile.privateDoor3)
                  : 0,
                profile.privateDoor4 && profile.privateDoor4 !== "00"
                  ? parseInt(profile.privateDoor4)
                  : 0,
              ],
            };
          });

        } else {
          profiles.value = [];
        }
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("dmp.common.error"),
            text: proxy.$t("dmp.profiles.messages.profilesLoadError"),
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
        profiles.value = [];
      }
    };

    const addNewProfile = () => {
      isEditing.value = false;
      selectedProfile.value = {
        profileNumber: "",
        name: "",
        armDisarmAreas: "",
        accessAreas: "",
        outputGroup: 0,
        reArmDelay: 0,
        inactiveUserAuditDays: 0,
        permissions: {
          arm: false,
          disarm: false,
          alarmSilence: false,
          lockdown: false,
          doorLockUnlock: false,
          sensorReset: false,
          doorAccess: false,
          armedAreas: false,
          outputsOnOff: false,
          zoneStatus: false,
          bypassZones: false,
          zoneMonitor: false,
          systemStatus: false,
          systemTest: false,
          profiles: false,
          userCodes: false,
          schedules: false,
          setTime: false,
          displayEvents: false,
          serviceRequest: false,
          fireDrill: false,
          extend: false,
          tempCode: false,
          antiPassback: false,
          easyArmDisarm: false,
          useSecondaryLanguage: false,
          cardPlusPIN: false,
          wiFiSetup: false,
          technicianUser: false,
          lockdownOverride: false,
          dualAuthority: false,
        },
        schedules: [0, 0, 0, 0, 0, 0, 0, 0],
        privateDoors: [0, 0, 0, 0],
      };
    };

    // Handle row selection
    const onRowSelected = (items) => {
      if (items.length > 0) {
        const selected = items[0];
        isEditing.value = true;
        selectedProfile.value = {
          profileNumber: parseProfileNumber(selected.profileNumber),
          name: selected.name || "",
          armDisarmAreas: selected.armDisarmAreas || "",
          accessAreas: selected.accessAreas || "",
          outputGroup: selected.outputGroup || 0,
          reArmDelay: selected.reArmDelay || 0,
          inactiveUserAuditDays: selected.inactiveUserAuditDays || 0,
          permissions: { ...selected.permissions },
          schedules: [...(selected.schedules || [0, 0, 0, 0, 0, 0, 0, 0])],
          privateDoors: [...(selected.privateDoors || [0, 0, 0, 0])],
        };
      } else {
        selectedProfile.value = null;
        isEditing.value = false;
      }
    };

    const deleteProfile = async () => {
      if (!isEditing.value) {
        return;
      }

      const result = await Swal.fire({
        title: proxy.$t("messages.delete.title"),
        text: proxy.$t("messages.delete.body"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: proxy.$t("actions.delete"),
        cancelButtonText: proxy.$t("actions.cancel"),
        customClass: {
          confirmButton: "btn btn-primary text-capitalize",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      });

      if (!result.isConfirmed) {
        return;
      }

      if (isTemplateMode.value) {
        const list = (props.profilesData || []).filter(
          (p) =>
            !p.profile ||
            parseInt(p.profile.profileNumber) !==
              parseInt(selectedProfile.value.profileNumber)
        );
        emitTemplateUpdate("profiles", list);
        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("dmp.common.success"),
            text: proxy.$t("dmp.profiles.messages.profileDeleteSuccess"),
            icon: "CheckIcon",
            variant: "success",
          },
        });
        selectedProfile.value = null;
        isEditing.value = false;
        return;
      }

      isDeletingProfile.value = true;
      store.commit("dmp_panels/setOperationActive", true);

      try {
        const response = await store.dispatch("dmp_panels/deleteProfile", {
          uuid: props.uuid,
          profileNumber: formatProfileNumber(selectedProfile.value.profileNumber),
        });

        if (response.success) {
          emit("operation-completed");
          toast({
            component: ToastificationContent,
            props: {
              title: proxy.$t("dmp.common.success"),
              text: proxy.$t("dmp.profiles.messages.profileDeleteSuccess"),
              icon: "CheckIcon",
              variant: "success",
            },
          });
          selectedProfile.value = null;
          isEditing.value = false;
        } else {
          throw new Error(response.message || "Failed to delete profile");
        }
      } catch (error) {
        emit("operation-error", error);
        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("dmp.common.error"),
            text: error.message || proxy.$t("dmp.profiles.messages.profileDeleteError"),
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        store.commit("dmp_panels/setOperationActive", false);
        isDeletingProfile.value = false;
      }
    };

    // Build a profile item in the exact GET shape (panel-config / template
    // JSON): zero-padded 3-digit numbers, "--" for empty schedules, GET-named
    // permission keys. Original permission object is spread first so GET-only
    // bits (unused3Bit2, reserved4Bit0, ...) are preserved untouched.
    const buildProfileGetItem = () => {
      const sp = selectedProfile.value;
      const pad3 = (v) => (parseInt(v) || 0).toString().padStart(3, "0");
      const sched = (v) =>
        !v || v === 0 ? "--" : parseInt(v).toString().padStart(2, "0");

      const original = (props.profilesData || []).find(
        (p) =>
          p.profile &&
          parseInt(p.profile.profileNumber) === parseInt(sp.profileNumber)
      );
      const basePerms =
        (original && original.profile && original.profile.permissions) || {};

      return {
        profile: {
          schedule1: sched(sp.schedules[0]),
          schedule2: sched(sp.schedules[1]),
          schedule3: sched(sp.schedules[2]),
          schedule4: sched(sp.schedules[3]),
          schedule5: sched(sp.schedules[4]),
          schedule6: sched(sp.schedules[5]),
          schedule7: sched(sp.schedules[6]),
          schedule8: sched(sp.schedules[7]),
          reArmDelay: pad3(sp.reArmDelay),
          accessAreas: sp.accessAreas || "",
          outputGroup: pad3(sp.outputGroup),
          permissions: {
            ...basePerms,
            arm: sp.permissions.arm,
            time: sp.permissions.setTime,
            wifi: sp.permissions.wiFiSetup,
            disarm: sp.permissions.disarm,
            easyArm: sp.permissions.easyArmDisarm,
            doorLock: sp.permissions.doorLockUnlock,
            lockdown: sp.permissions.lockdown,
            profiles: sp.permissions.profiles,
            fireDrill: sp.permissions.fireDrill,
            schedules: sp.permissions.schedules,
            userCodes: sp.permissions.userCodes,
            armedAreas: sp.permissions.armedAreas,
            doorAccess: sp.permissions.doorAccess,
            systemTest: sp.permissions.systemTest,
            zoneStatus: sp.permissions.zoneStatus,
            bypassZones: sp.permissions.bypassZones,
            cardPlusPin: sp.permissions.cardPlusPIN,
            outputOnOff: sp.permissions.outputsOnOff,
            sensorReset: sp.permissions.sensorReset,
            zoneMonitor: sp.permissions.zoneMonitor,
            alarmSilence: sp.permissions.alarmSilence,
            antiPassback: sp.permissions.antiPassback,
            systemStatus: sp.permissions.systemStatus,
            displayEvents: sp.permissions.displayEvents,
            dualAuthority: sp.permissions.dualAuthority,
            extendSchedule: sp.permissions.extend,
            serviceRequest: sp.permissions.serviceRequest,
            technicianUser: sp.permissions.technicianUser,
            lockdownOverride: sp.permissions.lockdownOverride,
            secondaryLanguage: sp.permissions.useSecondaryLanguage,
          },
          profileName: (sp.name || "").toUpperCase(),
          privateDoor1: pad3(sp.privateDoors[0]),
          privateDoor2: pad3(sp.privateDoors[1]),
          privateDoor3: pad3(sp.privateDoors[2]),
          privateDoor4: pad3(sp.privateDoors[3]),
          profileNumber: pad3(sp.profileNumber),
          armDisarmAreas: sp.armDisarmAreas || "",
          inactiveUserAuditDays: pad3(sp.inactiveUserAuditDays),
        },
      };
    };

    const saveProfile = async () => {
      const isValid = await detailFormObserver.value.validate();
      if (!isValid) {
        return;
      }

      // Validar si el número de perfil ya existe (solo para nuevos perfiles)
      if (!isEditing.value) {
        const profileExists = profiles.value.some(
          (p) => p.profileNumber === selectedProfile.value.profileNumber.toString()
        );

        if (profileExists) {
          toast({
            component: ToastificationContent,
            props: {
              title: proxy.$t("dmp.common.error"),
              text: proxy.$t("dmp.profiles.messages.profileNumberExists"),
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
          return;
        }
      }

      if (isTemplateMode.value) {
        const item = buildProfileGetItem();
        const list = [...(props.profilesData || [])];
        const idx = list.findIndex(
          (p) =>
            p.profile &&
            parseInt(p.profile.profileNumber) ===
              parseInt(item.profile.profileNumber)
        );
        if (idx !== -1) {
          item.id = list[idx].id != null ? list[idx].id : nextSequentialId(list);
          list.splice(idx, 1, item);
        } else {
          item.id = nextSequentialId(list);
          list.push(item);
        }

        emitTemplateUpdate("profiles", list);
        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("dmp.common.success"),
            text: isEditing.value
              ? proxy.$t("dmp.profiles.messages.profileUpdatedSuccess")
              : proxy.$t("dmp.profiles.messages.profileCreatedSuccess"),
            icon: "CheckIcon",
            variant: "success",
          },
        });
        selectedProfile.value = null;
        isEditing.value = false;
        return;
      }

      isSavingProfile.value = true;
      store.commit("dmp_panels/setOperationActive", true);

      try {
        const formatSchedule = (value) => {
          if (!value || value === 0) return "00";
          return parseInt(value).toString().padStart(2, "0");
        };

        const formatPrivateDoor = (value) => {
          if (!value || value === 0) return "000";
          return parseInt(value).toString().padStart(3, "0");
        };

        const profileToSave = {
          profileNumber: selectedProfile.value.profileNumber.toString(),
          profileName: selectedProfile.value.name.toUpperCase(),
          armDisarmAreas: selectedProfile.value.armDisarmAreas || "",
          accessAreas: selectedProfile.value.accessAreas || "",
          outputGroup: selectedProfile.value.outputGroup.toString().padStart(3, "0"),
          reArmDelay: selectedProfile.value.reArmDelay.toString(),
          inactiveUserAuditDays: selectedProfile.value.inactiveUserAuditDays.toString(),
          permissions: {
            arm: selectedProfile.value.permissions.arm,
            disarm: selectedProfile.value.permissions.disarm,
            alarmSilence: selectedProfile.value.permissions.alarmSilence,
            lockdown: selectedProfile.value.permissions.lockdown,
            doorLock: selectedProfile.value.permissions.doorLockUnlock,
            sensorReset: selectedProfile.value.permissions.sensorReset,
            doorAccess: selectedProfile.value.permissions.doorAccess,
            armedAreas: selectedProfile.value.permissions.armedAreas,
            outputOnOff: selectedProfile.value.permissions.outputsOnOff,
            zoneStatus: selectedProfile.value.permissions.zoneStatus,
            bypassZones: selectedProfile.value.permissions.bypassZones,
            zoneMonitor: selectedProfile.value.permissions.zoneMonitor,
            systemStatus: selectedProfile.value.permissions.systemStatus,
            systemTest: selectedProfile.value.permissions.systemTest,
            profiles: selectedProfile.value.permissions.profiles,
            userCodes: selectedProfile.value.permissions.userCodes,
            schedules: selectedProfile.value.permissions.schedules,
            time: selectedProfile.value.permissions.setTime,
            displayEvents: selectedProfile.value.permissions.displayEvents,
            serviceRequest: selectedProfile.value.permissions.serviceRequest,
            fireDrill: selectedProfile.value.permissions.fireDrill,
            extendSchedule: selectedProfile.value.permissions.extend,
            antiPassback: selectedProfile.value.permissions.antiPassback,
            easyArm: selectedProfile.value.permissions.easyArmDisarm,
            secondaryLanguage: selectedProfile.value.permissions.useSecondaryLanguage,
            cardPlusPin: selectedProfile.value.permissions.cardPlusPIN,
            wifi: selectedProfile.value.permissions.wiFiSetup,
            technicianUser: selectedProfile.value.permissions.technicianUser,
            lockdownOverride: selectedProfile.value.permissions.lockdownOverride,
            unused: false,
            reserved: false,
            dualAuthority: selectedProfile.value.permissions.dualAuthority,
          },
          schedule1: formatSchedule(selectedProfile.value.schedules[0]),
          schedule2: formatSchedule(selectedProfile.value.schedules[1]),
          schedule3: formatSchedule(selectedProfile.value.schedules[2]),
          schedule4: formatSchedule(selectedProfile.value.schedules[3]),
          schedule5: formatSchedule(selectedProfile.value.schedules[4]),
          schedule6: formatSchedule(selectedProfile.value.schedules[5]),
          schedule7: formatSchedule(selectedProfile.value.schedules[6]),
          schedule8: formatSchedule(selectedProfile.value.schedules[7]),
          privateDoor1: formatPrivateDoor(selectedProfile.value.privateDoors[0]),
          privateDoor2: formatPrivateDoor(selectedProfile.value.privateDoors[1]),
          privateDoor3: formatPrivateDoor(selectedProfile.value.privateDoors[2]),
          privateDoor4: formatPrivateDoor(selectedProfile.value.privateDoors[3]),
        };

        const response = await store.dispatch("dmp_panels/setProfile", {
          uuid: props.uuid,
          profileData: profileToSave,
        });

        if (response.success) {
          emit("operation-completed");
          toast({
            component: ToastificationContent,
            props: {
              title: proxy.$t("dmp.common.success"),
              text: isEditing.value
                ? proxy.$t("dmp.profiles.messages.profileUpdatedSuccess")
                : proxy.$t("dmp.profiles.messages.profileCreatedSuccess"),
              icon: "CheckIcon",
              variant: "success",
            },
          });
          selectedProfile.value = null;
          isEditing.value = false;
        } else {
          throw new Error(
            response.message || proxy.$t("dmp.profiles.messages.profileSaveError")
          );
        }
      } catch (error) {
        emit("operation-error", error);
        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("dmp.common.error"),
            text: error.message || proxy.$t("dmp.profiles.messages.profileSaveError"),
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        store.commit("dmp_panels/setOperationActive", false);
        isSavingProfile.value = false;
      }
    };

    watch(
      () => props.profilesData,
      (newData) => {
        if (newData && newData.length >= 0) {
          loadProfiles();
        }
      },
      { deep: true }
    );

    // Los time schedules y devices llegan por props (timeSchedulesData /
    // devicesData) y se consumen con computeds reactivos; no hay que hacer
    // fetch al activar el tab (las funciones fetchTimeSchedules/fetchDevices
    // se eliminaron al mover esos datos a props).

    onMounted(() => {
      loadProfiles();
    });

    return {
      profiles,
      isLoadingProfiles,
      isSavingProfile,
      isDeletingProfile,
      selectedProfile,
      isEditing,
      detailFormObserver,
      profileTableFields,

      loadProfiles,
      addNewProfile,
      onRowSelected,
      deleteProfile,
      saveProfile,
      formatProfileNumber,
      scheduleSelectOptions,
      privateDoorSelectOptions,
      refFormObserver,
      getValidationState,

      t,
      toast,
    };
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/variables/_variables.scss";

.card-header-custom {
  background-color: transparent;
}

.table-column,
.details-column {
  transition: all 0.3s ease-in-out;
}

.dark-layout {
  .card-header-custom {
    background-color: transparent !important;
  }
}

.gap-1 {
  gap: 0.25rem;
}

.mb-75 {
  margin-bottom: 0.75rem;
}

.font-weight-bold {
  font-weight: 600;
}
</style>
